/**
 * Arka plan görev tanımı (9.3.3.7 · D-588).
 *
 * Görev MODÜL düzeyinde tanımlanmak zorunda: işletim sistemi uygulamayı
 * arka planda yeniden başlattığında hiçbir ekran kurulmuş olmaz, yalnız
 * modüller yüklenir. Bir bileşenin içinde tanımlansaydı uyandırma boşa giderdi.
 *
 * Görevin kime ait olduğu da ekrandan gelemez; o yüzden paylaşım başlarken
 * kimlik ve jeton diske yazılıyor, görev oradan okuyor. Paylaşım bittiğinde
 * silinmesi bu yüzden önemli: silinmezse arka plan görevi, kapanmış bir
 * paylaşıma nokta göndermeye çalışır.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LOCATION_TASK } from './backgroundLocation';
import { decideSend, type Point } from './locationEnergy';

const ANAHTAR = 'live-location-share';

export type LiveShareHandle = { token: string; shareId: number; apiUrl: string };

export async function rememberLiveShare(handle: LiveShareHandle): Promise<void> {
  await AsyncStorage.setItem(ANAHTAR, JSON.stringify(handle));
}

export async function forgetLiveShare(): Promise<void> {
  await AsyncStorage.removeItem(ANAHTAR);
}

export async function readLiveShare(): Promise<LiveShareHandle | null> {
  try {
    const ham = await AsyncStorage.getItem(ANAHTAR);
    if (!ham) return null;
    const d = JSON.parse(ham);
    return typeof d?.token === 'string' && Number.isInteger(d?.shareId) && typeof d?.apiUrl === 'string'
      ? d as LiveShareHandle
      : null;
  } catch {
    return null;
  }
}

/** Son gönderilen nokta da diskte: arka planda bellek her uyandırmada boş. */
const SON_ANAHTAR = 'live-location-last';

export async function readLastSent(): Promise<{ point: Point; at: number } | null> {
  try {
    const ham = await AsyncStorage.getItem(SON_ANAHTAR);
    if (!ham) return null;
    const d = JSON.parse(ham);
    return typeof d?.point?.latitude === 'number' && typeof d?.at === 'number' ? d : null;
  } catch {
    return null;
  }
}

export async function writeLastSent(point: Point, at: number): Promise<void> {
  await AsyncStorage.setItem(SON_ANAHTAR, JSON.stringify({ point, at }));
}

/**
 * İşletim sisteminden gelen noktaları işler.
 *
 * Saf tutulamadı — diske ve ağa dokunuyor — ama KARAR saf katmandan geliyor
 * (`decideSend`), yani arka plan ile ön plan aynı kuralı paylaşıyor.
 */
export async function handleBackgroundLocations(
  noktalar: Array<{ coords: { latitude: number; longitude: number; accuracy?: number | null } }>,
  deps: {
    read: () => Promise<LiveShareHandle | null>;
    send: (h: LiveShareHandle, p: Point) => Promise<{ watching: boolean } | null>;
    stop: () => Promise<void>;
    lastSent: () => Promise<{ point: Point; at: number } | null>;
    remember: (p: Point, at: number) => Promise<void>;
    now: () => number;
  }
): Promise<void> {
  const son = noktalar[noktalar.length - 1];
  if (!son) return;
  const handle = await deps.read();
  // Paylaşım kapanmışsa görev de durmalı: aksi hâlde işletim sistemi
  // kimsenin beklemediği bir şey için cihazı uyandırmaya devam ederdi.
  if (!handle) { await deps.stop(); return; }

  const nokta: Point = {
    latitude: son.coords.latitude,
    longitude: son.coords.longitude,
    accuracy: son.coords.accuracy ?? null,
  };
  const onceki = await deps.lastSent();
  const gecen = onceki ? (deps.now() - onceki.at) / 1000 : Number.POSITIVE_INFINITY;
  if (decideSend({ current: nokta, lastSent: onceki?.point ?? null, secondsSinceLastSend: gecen }) !== 'send') {
    return;
  }

  const yanit = await deps.send(handle, nokta);
  if (!yanit) return;
  await deps.remember(nokta, deps.now());
  // Bakan yoksa arka planda ölçmeye devam etmenin anlamı yok; izleyen
  // haritayı açtığında sessiz bildirim cihazı yeniden başlatır (9.3.3.4).
  if (!yanit.watching) await deps.stop();
}

/** Görevi kaydeder. Uygulama açılışında BİR kez çağrılır. */
export function defineLiveLocationTask(): void {
  let TaskManager: typeof import('expo-task-manager') | null = null;
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    TaskManager = require('expo-task-manager');
  } catch {
    return;
  }
  if (!TaskManager || TaskManager.isTaskDefined(LOCATION_TASK)) return;

  TaskManager.defineTask(LOCATION_TASK, async ({ data, error }: any) => {
    if (error || !data?.locations) return;
    const { startBackgroundLocation, stopBackgroundLocation } = require('./backgroundLocation');
    void startBackgroundLocation;
    await handleBackgroundLocations(data.locations, {
      read: readLiveShare,
      send: async (h, p) => {
        try {
          const r = await fetch(`${h.apiUrl}/location-shares/${h.shareId}/point`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: h.token },
            body: JSON.stringify({
              latitude: p.latitude,
              longitude: p.longitude,
              ...(p.accuracy != null ? { accuracy_m: Math.round(p.accuracy) } : {}),
            }),
          });
          if (!r.ok) return { watching: false };
          return await r.json();
        } catch {
          return null;
        }
      },
      stop: stopBackgroundLocation,
      lastSent: readLastSent,
      remember: writeLastSent,
      now: () => Date.now(),
    });
  });
}
